<template>
  <l-map
    ref="elacMap"
    :zoom="map.zoom"
    :center="trackedCoords"
    :options="map.options"
  >
    <l-tile-layer
      v-for="tileProvider in tileProviders"
      :key="tileProvider.id"
      :name="tileProvider.name"
      :visible="tileProvider.visible"
      :url="tileProvider.url"
      :attribution="tileProvider.attribution"
      :options="tileProvider.options"
      layer-type="base"
    />

    <l-control-layers
      :position="layersPosition"
      :collapsed="false"
      :sort-layers="false"
    />

    <v-locatecontrol
      :options="locatecontrol"
    />

    <l-control-scale
      position="bottomleft"
      :imperial="false"
      :metric="true"
      :max-width="250"
    />

    <l-polyline
      v-for="d in devicesWithHistory"
      :key="d.id + '-history'"
      :lat-lngs="d.history"
      :color="d.color"
    />

    <v-rotated-marker
      v-for="d in devicesWithLocation"
      :key="d.id + '-location'"
      :lat-lng="d.location.latlng"
      :rotation-angle="d.location.heading"
      :icon="d.location.locationIcon"
    />

    <l-marker
      v-for="d in devicesWithLabels"
      :key="d.id + '-delay-label'"
      :lat-lng="d.location.latlng"
      :icon="d.location.labelIcon"
    />

    <l-layer-group
      v-for="layer in layers"
      :key="layer.id"
      layer-type="overlay"
      :name="layer.name"
    >
      <l-geo-json
        :geojson="layer.geojson"
        :options="geojsonOptions"
      />
    </l-layer-group>
  </l-map>
</template>

<script>
import {
  LMap,
  LTileLayer,
  LLayerGroup,
  LControlScale,
  LPolyline,
  LMarker,
  LGeoJson,
  LControlLayers,
} from 'vue2-leaflet';

import { createNamespacedHelpers } from 'vuex';
import Vue2LeafletRotatedMarker from 'vue2-leaflet-rotatedmarker';
import Vue2LeafletLocatecontrol from 'vue2-leaflet-locatecontrol';
import Vue from 'vue';
import TenantCurrent from '@/mixins/TenantCurrent';

const { mapState, mapGetters } = createNamespacedHelpers('devices');

const attribElac = '<a href="https://www.elac.ch" target="_blank">© Elac</a>';
const attribOSM = '<a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>';

const tileProviders = [
  /*
  {
    name: 'ELAC',
    visible: false,
    url: 'https://api.maptiler.com/maps/aaeca855-e472-4af2-b71a-54f646c280c3/{z}/{x}/{y}.png?key={accessToken}',
    attribution: `${attribElac} <a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a> ${attribOSM}`,
    options: {
      accessToken: '3rIU2gRipQbgDUYX5Cno',
      zoomOffset: -1,
      minZoom: 1,
      maxZoom: 20,
      crossOrigin: true,
      tileSize: 512,
    },
  },
  */
  {
    name: 'OSM CH',
    visible: true,
    url: 'https://tile.osm.ch/switzerland/{z}/{x}/{y}.png',
    attribution: `${attribElac} ${attribOSM}`,
    options: {
      minZoom: 1,
      maxZoom: 18,
      crossOrigin: true,
    },
  },
  {
    name: 'OSM Swiss',
    visible: false,
    url: 'https://tile.osm.ch/osm-swiss-style/{z}/{x}/{y}.png',
    attribution: `${attribElac} ${attribOSM}`,
    options: {
      minZoom: 1,
      maxZoom: 18,
      crossOrigin: true,
    },
  },
  {
    name: 'OSM DE',
    visible: false,
    url: 'https://tile.openstreetmap.de/{z}/{x}/{y}.png',
    attribution: `${attribElac} ${attribOSM}`,
    options: {
      minZoom: 1,
      maxZoom: 18,
      crossOrigin: true,
    },
  },
  {
    name: 'Open Topo',
    visible: false,
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: `${attribElac} ${attribOSM},
      <a href="http://viewfinderpanoramas.org">SRTM</a> |
      Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)`,
    options: {
      minZoom: 1,
      maxZoom: 16,
      crossOrigin: true,
    },
  },
];

export default {
  name: 'ElacMap',

  components: {
    LMap,
    LTileLayer,
    LLayerGroup,
    LControlScale,
    LPolyline,
    LMarker,
    LGeoJson,
    LControlLayers,
    'v-rotated-marker': Vue2LeafletRotatedMarker,
    'v-locatecontrol': Vue2LeafletLocatecontrol,
  },

  mixins: [TenantCurrent],

  data() {
    return {
      map: {
        zoom: 11,
        options: {
          zoomSnap: 0,
          zoomControl: true,
        },
      },

      tileProviders,
      layersPosition: 'topleft',

      locatecontrol: {
        setView: 'always',
        keepCurrentZoomLevel: true,
        drawCircle: false,
        showPopup: false,
        enableHighAccuracy: true,
        icon: 'mdi mdi-crosshairs-gps',
        iconLoading: 'mdi mdi-loading mdi-spin',
      },

      layers: [],

      geojsonOptions: {
        style(geoJsonFeature) {
          return {
            color: geoJsonFeature.properties.stroke || '#ec076e',
            weight: geoJsonFeature.properties['stroke-width'] || 4,
          };
        },

        pointToLayer(geoJsonPoint, latlng) {
          if (!geoJsonPoint.properties || !geoJsonPoint.properties.icon) {
            return L.marker(latlng);
          }

          return L.marker(latlng, {
            icon: L.icon({
              iconUrl: geoJsonPoint.properties.icon,
              iconSize: [32, 32], // size of the icon
              iconAnchor: [16, 32], // point of the icon which will correspond to marker's location
            }),
          });
        },

        onEachFeature(feature, layer) {
          if (feature.properties.name) {
            layer.bindPopup(`<p><b>${feature.properties.name}</b></p>`);
          }
        },
      },
    };
  },

  computed: {
    ...mapState([
      'devices',
    ]),

    ...mapGetters([
      'devicesWithLabels',
      'devicesWithLocation',
      'devicesWithHistory',
      'trackedCoords',
    ]),
  },

  beforeMount() {
    Vue.prototype.$api.get(`/tenants/${this.tenant.id}/layers`)
      .then((result) => {
        Vue.set(this, 'layers', result.data);
      });
  },
};
</script>

<style>
@import "~leaflet/dist/leaflet.css";

.device-marker-inactive {
  opacity: 0.5;
}

.device-marker .device-marker-svg {
  height: 20px;
  width: 20px;
  stroke: black;
  stroke-width: 35;
}

.device-marker .device-name {
  font-size: 13px;
  font-weight: bold;
}

.device-label {
  border: 2px solid gray;
  background: white;
  border-radius: 10px;
  text-align: center;
  vertical-align: middle;
  font-weight: bold;
  font-size: 1.2em;
}
</style>
